import { StyleDeclarationValue, css } from 'aphrodite';
import { observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { useState } from 'react';
import { Waypoint } from 'react-waypoint';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { getDisplayName } from '../../../../models/NameUtils';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { useErrorMessages } from '../../../hooks';
import { GoalsViewModel, IGoals, UserDailyActivity } from '../../../viewModels/goals';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	styles?: StyleDeclarationValue[];
	goals: GoalsViewModel;
}

interface IData {
	name: string;

	values: IGoals;
}

const EXTRA_DATA_ROWS: [string, keyof IGoals][] = [
	['Points', 'points'],
	['Dials', 'dials'],
	['Connections', 'connections'],
	['Demos', 'demos'],
	['Max Cold Demos', 'maxColdDemos'],
	['Max Follow Ups', 'maxFollowUps'],
	['Max Oldies', 'maxOldies'],
	['Max Research', 'maxResearch'],
	['Adjusted Dials', 'adjustedDials'],
	['Adjusted Demos', 'adjustedDemos'],
];

const columnTitles = (isSalesCoffeeAccount: boolean): string[] => [
	'User',
	'Day',
	'Goals',
	'Score',
	'Projected',
	'Role',
	'Teams',
	'Lead Pool',
	...(isSalesCoffeeAccount ? ['Quota Team', 'Office'] : ['Sub Lead Pools']),
	'Last Modified',
];

// Widths in px; null lets the Sub Lead Pools column take the rest of the row.
const columnWidths = (isSalesCoffeeAccount: boolean): (number | null)[] => [
	128,
	104,
	72,
	72,
	96,
	80,
	104,
	96,
	...(isSalesCoffeeAccount ? [96, 104] : [null]),
	168,
];

export const GoalsTable: React.FC<IProps> = observer(props => {
	const { goals, className, styles = [] } = props;
	const userSession = useUserSession();
	const isSalesCoffeeAccount = !!userSession?.account?.isLevitateSalesCoffeeAccount;
	const errorMessages = useErrorMessages();
	const [extraData, setExtraData] = useState<IData>(null);

	const loadMore = () => {
		goals.loadNext()?.catch(err => errorMessages.pushApiError(err));
	};

	const setData = (data: IData) => () => {
		setExtraData(data);
	};

	return (
		<>
			<table className={`${css(styleSheet.container, ...styles)} goals-table ${className || ''}`}>
				<colgroup>
					{columnWidths(isSalesCoffeeAccount).map((width, index) => (
						<col key={index} style={width ? { width } : undefined} />
					))}
				</colgroup>
				<thead>
					<tr>
						{columnTitles(isSalesCoffeeAccount).map(title => (
							<th key={title} className={css(styleSheet.columnHeader)}>
								{title}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{goals.items?.map((x, index) => (
						<GoalsRow
							key={x.id}
							da={x}
							hasDivider={index > 0}
							setExtraData={setData}
							isSalesCoffeeAccount={isSalesCoffeeAccount}
						/>
					))}
					<tr>{goals.items?.length > 0 && <Waypoint bottomOffset='-200px' onEnter={loadMore} />}</tr>
					<tr>{goals.isLoading && <LoadingSpinner />}</tr>
				</tbody>
			</table>
			{extraData && (
				<div className={css(styleSheet.extraData)}>
					<div className={css(styleSheet.extraDataName)}>{extraData.name}</div>
					{EXTRA_DATA_ROWS.map(([label, key]) => (
						<div key={key} className={css(styleSheet.extraDataRow)}>
							<div className={css(styleSheet.extraDataKey)}>{label}</div>
							<div>{extraData.values?.[key]}</div>
						</div>
					))}
				</div>
			)}
		</>
	);
});

interface IRowProps {
	className?: string;
	styles?: StyleDeclarationValue[];
	da: UserDailyActivity;
	setExtraData(data: IData): () => void;
	isSalesCoffeeAccount: boolean;
	hasDivider?: boolean;
}

export const GoalsRow: React.FC<IRowProps> = observer(props => {
	const { da, className, setExtraData, styles = [], isSalesCoffeeAccount, hasDivider } = props;
	// A `border-top` on the row does not paint under `border-collapse: separate`, so each cell carries it.
	const cell = hasDivider ? [styleSheet.cell, styleSheet.cellDivider] : [styleSheet.cell];

	return (
		<tr className={`${css(...styles)} goals-row ${className || ''}`}>
			<td className={css(...cell)} title={getDisplayName(da.creator)}>
				{getDisplayName(da.creator)}
			</td>
			<td className={css(...cell)} title={da.day}>
				{moment(da.day).format('MM-DD-yyyy')}
			</td>
			<td
				className={css(...cell, styleSheet.cellInteractive)}
				onMouseEnter={da.goals && setExtraData({ name: 'Goals', values: da.goals })}
				onMouseLeave={setExtraData(null)}
			>
				{da.goals && 'data'}
			</td>
			<td
				className={css(...cell, styleSheet.cellInteractive)}
				onMouseEnter={da.score && setExtraData({ name: 'Score', values: da.score })}
				onMouseLeave={setExtraData(null)}
			>
				{da.score && 'data'}
			</td>
			<td
				className={css(...cell, styleSheet.cellInteractive)}
				onMouseEnter={
					da.projectedScore &&
					setExtraData({
						name: 'Current Projected Score',
						values: da.projectedScore,
					})
				}
				onMouseLeave={setExtraData(null)}
			>
				{da.projectedScore && 'data'}
			</td>
			<td className={css(...cell)} title={da.role}>
				{da.role}
			</td>
			<td className={css(...cell)} title={da.teams?.join(', ')}>
				{da.teams?.join(', ')}
			</td>
			<td className={css(...cell)} title={da.vertical?.industry}>
				{da.vertical?.industry}
			</td>
			{isSalesCoffeeAccount ? (
				<>
					<td className={css(...cell)} title={da.quotaTeam}>
						{da.quotaTeam}
					</td>
					<td className={css(...cell)} title={da.officeLocation}>
						{da.officeLocation}
					</td>
				</>
			) : (
				<td className={css(...cell)} title={da.vertical?.subverticals?.join(', ')}>
					{da.vertical?.subverticals?.join(', ')}
				</td>
			)}
			<td className={css(...cell)} title={da.lastModifiedDate}>
				{moment(da.lastModifiedDate).format('MM-DD-yyyy h:mm a')}
			</td>
		</tr>
	);
});
